/* Fiscal Certificate (A1 .pfx) — upload/replace/delete card.
 * Extracted to a standalone file to avoid circular imports between
 * ERPDashboard.js and NaturezaOperacaoPage.jsx.
 *
 * iter156c: aceita `companyId` para escopo por filial + botao "Compartilhar" com outras.
 */
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Share2, X, Check } from 'lucide-react';
import { API } from '../lib/api';
import { confirmDialog } from './ConfirmDialog';

export const FiscalCertCard = ({
  cardCls = "bg-white rounded-2xl border border-slate-200 p-5",
  labelCls = "block text-xs font-bold text-slate-600 mb-1",
  companyId = null,
  companies = [],
}) => {
  const [cert, setCert] = useState(null);
  const [loading, setLoading] = useState(true);
  const [file, setFile] = useState(null);
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [showShare, setShowShare] = useState(false);

  const fetchCert = useCallback(async () => {
    setLoading(true);
    try {
      const url = companyId
        ? `${API}/erp/fiscal-cert?company_id=${encodeURIComponent(companyId)}`
        : `${API}/erp/fiscal-cert`;
      const { data } = await axios.get(url, { withCredentials: true });
      setCert(data?.data || null);
    } catch { setCert(null); }
    finally { setLoading(false); }
  }, [companyId]);
  useEffect(() => { fetchCert(); }, [fetchCert]);

  const upload = async (e) => {
    e.preventDefault();
    if (!file) return toast.error('Selecione o arquivo .pfx');
    if (!password) return toast.error('Informe a senha do certificado');
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append('pfx_file', file);
      fd.append('password', password);
      if (companyId) fd.append('company_id', companyId);
      const { data } = await axios.post(`${API}/erp/fiscal-cert`, fd, { withCredentials: true });
      toast.success(`Certificado salvo (CNPJ ${data.data?.cnpj_cpf || '—'})${companyId ? ' para esta filial' : ''}`);
      setFile(null); setPassword('');
      await fetchCert();
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message || 'Erro ao salvar certificado');
    } finally { setBusy(false); }
  };

  const remove = async () => {
    if (!(await confirmDialog({ title: `Excluir o certificado A1${companyId ? ' desta filial' : ' deste tenant'}? Acao irreversivel.`, confirmLabel: 'Confirmar', danger: true }))) return;
    try {
      const url = companyId
        ? `${API}/erp/fiscal-cert?company_id=${encodeURIComponent(companyId)}`
        : `${API}/erp/fiscal-cert`;
      await axios.delete(url, { withCredentials: true });
      toast.success('Certificado removido');
      await fetchCert();
    } catch { toast.error('Erro ao excluir'); }
  };

  const isOwnCert = !!(companyId && cert && cert.company_id === companyId);
  const isSharedCert = !!(companyId && cert && !isOwnCert && ((cert.shared_company_ids || []).includes(companyId) || !cert.company_id));

  const fmtDate = (s) => s ? new Date(s).toLocaleString('pt-BR') : '—';
  const daysUntilExpire = cert?.valid_until ? Math.ceil((new Date(cert.valid_until) - new Date()) / (1000*60*60*24)) : null;

  // iter77cy: botao "Compartilhar" no cantinho do header — sempre visivel quando
  // o cert e desta filial e existem outras filiais (facil descoberta).
  const canShare = !!(cert && cert.id && isOwnCert && companies.length > 1);

  return (
    <div className={cardCls} data-testid="fiscal-cert-card">
      <div className="flex items-start justify-between mb-2 gap-2">
        <h3 className="font-bold text-gray-900">Certificado Digital A1 (NF-e/NFC-e){companyId ? ' — Filial ativa' : ''}</h3>
        {canShare && (
          <button
            type="button"
            onClick={() => setShowShare(true)}
            title="Compartilhar este certificado com outras filiais"
            className="inline-flex items-center justify-center w-8 h-8 rounded-lg text-cyan-700 hover:bg-cyan-50 hover:text-cyan-900 border border-cyan-200 hover:border-cyan-400 transition-colors shrink-0"
            data-testid="fiscal-cert-share-icon"
          >
            <Share2 size={14} />
          </button>
        )}
      </div>
      <p className="text-xs text-gray-500 mb-4">Faca upload do arquivo <code className="bg-slate-100 px-1 rounded">.pfx</code> da Receita Federal/AC para emitir notas fiscais. A senha e armazenada criptografada. Cada filial pode ter seu proprio certificado; use o botao <Share2 size={10} className="inline mb-0.5" /> no canto para liberar em outras filiais.</p>

      {loading ? (
        <div className="py-4 text-center text-xs text-slate-400">Carregando...</div>
      ) : cert ? (
        <div className="mb-4 bg-gradient-to-br from-emerald-50 to-cyan-50 border border-emerald-200 rounded-xl p-4" data-testid="fiscal-cert-info">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-xs">
            <div><span className="font-bold text-slate-600">Titular:</span> {cert.cn || '—'}</div>
            <div><span className="font-bold text-slate-600">CNPJ/CPF:</span> <span className="font-mono">{cert.cnpj_cpf || '—'}</span></div>
            <div><span className="font-bold text-slate-600">Emissor:</span> {cert.issuer_cn || '—'}</div>
            <div><span className="font-bold text-slate-600">Valido ate:</span> <span className={cert.expired ? 'text-rose-600 font-bold' : daysUntilExpire != null && daysUntilExpire <= 30 ? 'text-amber-600 font-bold' : 'text-emerald-700 font-bold'}>{fmtDate(cert.valid_until)} {daysUntilExpire != null && !cert.expired && `(${daysUntilExpire}d)`}</span></div>
            <div><span className="font-bold text-slate-600">Upload em:</span> {fmtDate(cert.uploaded_at)}</div>
            <div><span className="font-bold text-slate-600">Arquivo:</span> <span className="font-mono">{cert.filename}</span></div>
            {companyId && (
              <div className="md:col-span-2">
                <span className="font-bold text-slate-600">Escopo:</span>{' '}
                {isOwnCert && <span className="inline-block text-[10px] font-black uppercase bg-violet-100 text-violet-700 border border-violet-300 px-1.5 py-0.5 rounded">CERT DESTA FILIAL</span>}
                {isSharedCert && <span className="inline-block text-[10px] font-black uppercase bg-cyan-100 text-cyan-800 border border-cyan-300 px-1.5 py-0.5 rounded">{cert.company_id ? 'HERDADO (COMPARTILHADO)' : 'LEGADO (GLOBAL)'}</span>}
                {(cert.shared_company_ids || []).length > 0 && <span className="ml-2 inline-block text-[10px] font-black uppercase bg-emerald-100 text-emerald-700 border border-emerald-300 px-1.5 py-0.5 rounded" title={`Compartilhado com ${cert.shared_company_ids.length} filial(is)`}>+{cert.shared_company_ids.length} filial(is)</span>}
              </div>
            )}
          </div>
          {cert.expired && <div className="mt-3 text-xs font-bold text-rose-700 bg-rose-50 border border-rose-200 rounded-lg px-3 py-2">⚠ Certificado VENCIDO. Faca upload de um novo.</div>}
          {!cert.expired && daysUntilExpire != null && daysUntilExpire <= 30 && (
            <div className="mt-3 text-xs font-bold text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">⚠ Certificado vence em {daysUntilExpire} dias. Renove antes do vencimento.</div>
          )}
          <div className="mt-4 flex justify-end gap-2">
            {isOwnCert && cert.id && companies.length > 1 && (
              <button type="button" onClick={() => setShowShare(true)} className="inline-flex items-center gap-1 text-xs text-cyan-700 hover:text-cyan-900 font-bold" data-testid="fiscal-cert-share">
                <Share2 size={12} /> Compartilhar com outras filiais
              </button>
            )}
            {(isOwnCert || !companyId) && (
              <button type="button" onClick={remove} className="text-xs text-rose-600 hover:text-rose-700 font-bold" data-testid="fiscal-cert-delete">Remover certificado</button>
            )}
          </div>
        </div>
      ) : (
        <div className="mb-4 bg-amber-50 border border-amber-200 rounded-xl p-3 text-xs text-amber-900">Nenhum certificado configurado{companyId ? ' para esta filial' : ''}. Faca upload abaixo para habilitar a emissao de NF-e/NFC-e.</div>
      )}

      {/* iter77cy: sempre exibe o form de upload — mesmo quando o cert atual e "compartilhado"
          ou "legado global" (sem company_id), o usuario precisa poder subir um novo
          (especialmente quando vencido). Uploading enquanto usa um cert herdado cria um
          novo cert especifico desta filial (sobrepoe o herdado). */}
      {(
        <form onSubmit={upload} className="space-y-3" data-testid="fiscal-cert-upload-form">
          {isSharedCert && cert && (
            <div className="text-[11px] text-cyan-800 bg-cyan-50 border border-cyan-200 rounded-lg px-3 py-2">
              Voce esta usando um certificado {cert.company_id ? 'compartilhado' : 'legado (global)'}. Ao subir um .pfx aqui, sera criado um <strong>certificado exclusivo desta filial</strong> (sobrepoe o herdado).
            </div>
          )}
          <div>
            <label className={labelCls}>Arquivo .pfx</label>
            <input type="file" accept=".pfx,.p12" onChange={e => setFile(e.target.files?.[0] || null)} className="block w-full text-xs text-slate-600 file:mr-3 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-xs file:font-bold file:bg-violet-50 file:text-violet-700 hover:file:bg-violet-100" data-testid="fiscal-cert-file" />
          </div>
          <div>
            <label className={labelCls}>Senha do certificado</label>
            <input type="password" value={password} onChange={e => setPassword(e.target.value)} className="w-full px-3 py-2 bg-white border border-slate-300 rounded-lg text-sm text-slate-900 focus:outline-none focus:border-violet-600" placeholder="••••••••" data-testid="fiscal-cert-password" />
          </div>
          <button type="submit" disabled={busy || !file || !password} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-violet-600 hover:bg-violet-700 text-white text-sm font-bold disabled:opacity-50" data-testid="fiscal-cert-upload">
            {busy ? 'Enviando...' : (cert && isOwnCert ? 'Substituir certificado' : (cert && isSharedCert ? 'Enviar certificado desta filial' : 'Enviar certificado'))}
          </button>
        </form>
      )}
      {showShare && cert && cert.id && (
        <CertShareModal
          cert={cert}
          companies={companies}
          onClose={() => setShowShare(false)}
          onSaved={() => { setShowShare(false); fetchCert(); }}
        />
      )}
    </div>
  );
};

/* iter156c: Modal de compartilhamento do certificado A1 com outras filiais. */
const CertShareModal = ({ cert, companies = [], onClose, onSaved }) => {
  const initSelected = useMemo(() => new Set(cert.shared_company_ids || []), [cert.shared_company_ids]);
  const [selected, setSelected] = useState(initSelected);
  const [saving, setSaving] = useState(false);
  const activeCompanies = companies.filter(c => c.active !== false && c.id !== cert.company_id);
  const toggle = (id) => setSelected(prev => { const n = new Set(prev); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  const markAll = () => setSelected(new Set(activeCompanies.map(c => c.id)));
  const clearAll = () => setSelected(new Set());
  const save = async () => {
    setSaving(true);
    try {
      const { data } = await axios.post(`${API}/erp/fiscal-cert/${cert.id}/share`, { company_ids: Array.from(selected) }, { withCredentials: true });
      const overwritten = (data?.overwritten_company_ids || []).length;
      if (overwritten > 0) {
        toast.success(`Compartilhado. ${overwritten} filial(is) tinham cert proprio — foram sobrescritas pelo compartilhado.`);
      } else {
        toast.success('Compartilhamento atualizado');
      }
      onSaved?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao compartilhar');
    } finally { setSaving(false); }
  };
  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="cert-share-modal">
      <div className="bg-white rounded-2xl max-w-lg w-full max-h-[85vh] overflow-hidden shadow-2xl flex flex-col">
        <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between bg-gradient-to-r from-cyan-50 to-violet-50">
          <div>
            <h2 className="font-black text-slate-900 text-base flex items-center gap-2"><Share2 size={16} className="text-cyan-600" /> Compartilhar Certificado A1</h2>
            <p className="text-[11px] text-slate-500 mt-0.5">CNPJ {cert.cnpj_cpf || '—'} · {cert.cn || cert.filename}</p>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700" data-testid="cert-share-close"><X size={18} /></button>
        </div>
        <div className="p-5 flex-1 overflow-y-auto">
          <p className="text-xs text-slate-600 mb-3">Marque as filiais que <strong>tambem</strong> poderao usar este certificado para emitir NF-e/NFC-e. Sem marcar, ele fica <strong>exclusivo da filial dona</strong>.</p>
          <div className="mb-3 text-[11px] text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
            <strong>Atencao:</strong> Se a filial marcada ja possuir um certificado proprio, ele sera <strong>sobrescrito</strong> por este compartilhado (o cert proprio da filial destino sera removido).
          </div>
          <div className="flex gap-2 mb-3">
            <button type="button" onClick={markAll} className="text-[11px] font-bold text-violet-700 hover:text-violet-900" data-testid="cert-share-all">Marcar todas</button>
            <span className="text-slate-300">|</span>
            <button type="button" onClick={clearAll} className="text-[11px] font-bold text-slate-500 hover:text-slate-700" data-testid="cert-share-none">Desmarcar todas</button>
          </div>
          <div className="space-y-1 max-h-[50vh] overflow-y-auto">
            {activeCompanies.length === 0 && <p className="text-xs text-slate-400 py-4 text-center">Nenhuma outra filial disponivel.</p>}
            {activeCompanies.map(c => {
              const checked = selected.has(c.id);
              return (
                <label key={c.id} className={`flex items-center gap-3 p-2.5 rounded-lg border cursor-pointer transition-colors ${checked ? 'bg-cyan-50 border-cyan-300' : 'bg-white border-slate-200 hover:bg-slate-50'}`} data-testid={`cert-share-company-${c.id}`}>
                  <input type="checkbox" checked={checked} onChange={() => toggle(c.id)} className="w-4 h-4 accent-cyan-600" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-bold text-slate-900 truncate">{c.code} · {c.name}{c.is_matriz && <span className="ml-2 text-[9px] font-black bg-violet-100 text-violet-700 px-1.5 py-0.5 rounded">MATRIZ</span>}</p>
                    <p className="text-[11px] text-slate-500 truncate">{c.cnpj || 'Sem CNPJ'}</p>
                  </div>
                  {checked && <Check size={16} className="text-cyan-600" />}
                </label>
              );
            })}
          </div>
        </div>
        <div className="px-5 py-3 border-t border-slate-200 bg-slate-50 flex items-center justify-end gap-2">
          <span className="text-[11px] text-slate-500 mr-auto">{selected.size} filial(is) selecionada(s)</span>
          <button onClick={onClose} className="px-4 py-2 text-sm text-slate-500 hover:text-slate-700" data-testid="cert-share-cancel">Cancelar</button>
          <button onClick={save} disabled={saving} className="inline-flex items-center gap-1.5 px-5 py-2 rounded-lg bg-cyan-600 hover:bg-cyan-700 text-white text-sm font-bold disabled:opacity-50" data-testid="cert-share-save">{saving ? 'Salvando...' : 'Salvar'}</button>
        </div>
      </div>
    </div>
  );
};

export default FiscalCertCard;
